// Core
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

// Chakra
import {

  Text,
  useColorModeValue,
  Divider, Button, Flex, Table, Thead, Tr, Td, Tbody, Switch,
} from '@chakra-ui/react';

// Assets
import { apiService } from 'services/ApiService';

// Components
import NotFound from 'views/Dashboard/404.js';
import Pagination from '../../../components/Pagination';
import { useHistory } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa6';
import Card from '../../../components/Card/Card';
import TableSkeleton from '../../../components/TableSkeleton';
import { DeleteIcon } from 'components/Icons/Icons';
import Attention from '../../../components/Modals/Attention';

const ApiKey = () => {

  const history = useHistory();
  const borderColor = useColorModeValue('blue.50', 'rgba(255, 255, 255, 0.10)');

  const textColor = useColorModeValue('gray.700', 'white');
  const paddingTd = '16px 0 16px 24px';
  const [offset, setOffset] = useState(0);
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeysMeta, setApiKeysMeta] = useState([]);
  const limit = 25;

  const { isLoading: isApiKeysLoading, isFetching, refetch } = useQuery({
    queryKey: ['api-keys', offset],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const params = {
        limit,
        offset,
      };

      const response = await apiService.getApiKeys(params);
      setApiKeys(response.data);
      setApiKeysMeta(response.meta);
      return true;
    },
    retry: false,
    refetchInterval: 30000,
  });

  const onNewButtonClicked = useCallback(() => {
    history.push('/api-keys/new');
  }, [history]);

  const {isLoading, mutate} = useMutation({
    mutationFn: (apiKey) =>
      apiService.updateApiKey({ id:apiKey.id, isActive: !apiKey.isActive }),
    onSuccess: ()=>{
      if(refetch){
        refetch();
      }
    }
  });

  const [selectedKeyId, setSelectedKeyId] = useState();

  const handleDelete = useCallback(
    async (keyId) => {
      await apiService.deleteApiKey({ id: keyId });
      refetch();
    },
    [refetch],
  );

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontSize='40px' fontStyle='normal' fontWeight='700' color={textColor} mb='0px' className="page-name">API Keys</Text>
        <Button
          variant="submit-ms"
          maxW="140px"
          w="100%"
          onClick={onNewButtonClicked}
          leftIcon={<FaPlus />}
        >
          Create
        </Button>
      </Flex>

      <Divider opacity="1" mr="2px" borderColor={borderColor} borderTopWidth="1px" borderBottomWidth="0" mb='32px'/>

      {isApiKeysLoading ? (
        <TableSkeleton rowCount={5} />
      )
      :apiKeys.length > 0 ? (
        <Card padding={'8px'}>
          <Table color={textColor}>
            <Thead>
              <Tr>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Name
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Key
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Partner
                  </Text>
                </Td>
                <Td>
                  <Text variant="title-md" p="0 0 6px 0">
                    Active
                  </Text>
                </Td>
                <Td>
                </Td>
              </Tr>
            </Thead>
            <Tbody>
              {apiKeys.map((row) => {
                return (
                  <Tr
                    _last={{
                    '& td': {
                      borderBottom: 'none !important',
                    },
                  }} key={row.id} border="unset" className="cursor-tr">
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.key}
                      </Text>
                    </Td>
                    <Td  verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.name}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <Text variant="text" color={textColor}>
                        {row?.partnerId}
                      </Text>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                        <Switch
                          disabled={isLoading}
                          isChecked={row?.isActive}
                          colorscheme="teal"
                          onChange={()=>mutate(row)}/>
                    </Td>
                    <Td verticalAlign="center" p={paddingTd}>
                      <DeleteIcon onClick={()=>setSelectedKeyId(row.id)} color="#DE3730" fontSize="24px" />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Card>
      ) : (
        <NotFound text="Not Found" />
      )}

      <Pagination
        meta={apiKeysMeta ?? { limit, offset, total: 0 }}
        isLoadingPage={isFetching}
        currentPage={Math.floor(offset / limit) + 1}
        onPageChange={(newOffset) => setOffset(newOffset)}
      />

      <Attention
        variant="remove"
        handle={() => handleDelete(selectedKeyId)}
        isOpen={!!selectedKeyId}
        onClose={()=>setSelectedKeyId(null)}
      />

    </>
  );
};

export default ApiKey;
